import * as React from 'react';
import { getFormatDateFromObject, getDateFromString, getFormatDateFromStoreDate } from 'shared-components';
import {
  BiometricDocumentList,
  DocAccessType,
  OtherVisaList,
  OtherVisaMap,
  StudentVisaDocList,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { boolToYesNo } from '../../../../utilities/formatting';
import { getVisaLabelFromValue } from 'data';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: OtherVisaMap;
  warning?: string;
  userRole?: UserRole;

  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;

  onResume: (url: string) => void;

  onVisaUploadClick: (visaKey: string, docKey: string) => void;
  onVisaOpenClick: (visaKey: string, docKey: string) => void;
  onVisaApproveClick: (visaKey: string, docKey: string) => void;
  onVisaRejectClick: (visaKey: string, docKey: string) => void;
  onVisaRescindRejectClick: (visaKey: string, docKey: string) => void;
  onVisaDeleteClick: (visaKey: string, docKey: string) => void;
  onVisaAcknowledgeClick: (visaKey: string, docKey: string) => void;

  onBiometricUploadClick: (bioKey: string, docKey: string) => void;
  onBiometricOpenClick: (bioKey: string, docKey: string) => void;
  onBiometricApproveClick: (bioKey: string, docKey: string) => void;
  onBiometricRejectClick: (bioKey: string, docKey: string) => void;
  onBiometricRescindRejectClick: (bioKey: string, docKey: string) => void;
  onBiometricDeleteClick: (bioKey: string, docKey: string) => void;
  onBiometricAcknowledgeClick: (bioKey: string, docKey: string) => void;
}

const OtherVisa = (props: IProps) => {
  const { documents: visas = {}, infoOnly, userAccess, userRole } = props;

  return (
    <>
      {Object.entries(visas)
        .sort()
        .map(([visasKey, otherVisa]) => {
          let hasDocs = false;
          let hasDeletedDocs = false;
          const docNodes: React.ReactNode[] = [];

          if (otherVisa.visa) {
            const visibleDocs = getVisible({
              documentsHash: otherVisa.visa,
              userAccess,
              infoOnly,
              userRole,
            }) as StudentVisaDocList[];
            if (hasDeleted(otherVisa.visa)) {
              hasDeletedDocs = true;
            }
            if (visibleDocs.length > 0) {
              hasDocs = true;
              docNodes.push(
                ...visibleDocs.map((doc: OtherVisaList, visaIndex: number) => {
                  const { key: docKey, item } = doc;
                  return (
                    <FingerWithApprove
                      key={`visa-other-${docKey}`}
                      status={getFingerStatus(item)}
                      isProcessingPdf={item.isProcessingPdf}
                      reason={item.reason}
                      fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                      fileNames={item.fileNames}
                      label={`Previous Visa ${getVisaLabelFromValue(item.visaType)} - ${item.year} ${
                        Object.keys(otherVisa.visa ?? {}).length > 1 ? (visaIndex > 0 ? visaIndex + 1 : '') : ''
                      }`}
                      canUpload={infoOnly ? false : props.canUpload}
                      canOpen={infoOnly ? false : props.canOpen}
                      canApprove={infoOnly ? false : props.canApprove}
                      canReject={infoOnly ? false : props.canReject}
                      isDeleting={infoOnly ? false : props.isDeleting}
                      canAcknowledge={infoOnly ? props.canAcknowledge : false}
                      onUploadClick={() => props.onVisaUploadClick(visasKey, docKey)}
                      onOpenClick={() => props.onVisaOpenClick(visasKey, docKey)}
                      onApproveClick={() => props.onVisaApproveClick(visasKey, docKey)}
                      onRejectClick={() => props.onVisaRejectClick(visasKey, docKey)}
                      onRescindRejectClick={() => props.onVisaRescindRejectClick(visasKey, docKey)}
                      onDeleteClick={() => props.onVisaDeleteClick(visasKey, docKey)}
                      onAcknowledgeClick={() => props.onVisaAcknowledgeClick(visasKey, docKey)}
                      userRole={props.userRole}
                      deletedByTTL={item.deletedByTTL}
                    >
                      {item.startDate && item.endDate ? (
                        <>
                          <SummaryRow
                            key={`other-visa-start-date-${docKey}`}
                            label="Start Date"
                            fullLabel="Enter the start date for this visa (as stated on the visa itself)"
                            value={`${getFormatDateFromObject(getDateFromString(item.startDate))}`}
                          />

                          <SummaryRow
                            key={`other-visa-end-date-${docKey}`}
                            label="End Date"
                            fullLabel="Enter the end date for this visa (as stated on the visa itself)"
                            value={`${getFormatDateFromObject(getDateFromString(item.endDate))}`}
                          />
                        </>
                      ) : undefined}
                    </FingerWithApprove>
                  );
                }),
              );
            }
          }

          if (otherVisa.biometric) {
            const visibleDocs = getVisible({
              documentsHash: otherVisa.biometric,
              userAccess,
              infoOnly,
              userRole,
            }) as BiometricDocumentList[];
            if (hasDeleted(otherVisa.biometric)) {
              hasDeletedDocs = true;
            }
            if (visibleDocs.length > 0) {
              hasDocs = true;
              docNodes.push(
                ...visibleDocs.map((bio: BiometricDocumentList, index: number) => {
                  const { key: docKey, item } = bio;
                  return (
                    <FingerWithApprove
                      key={`other-visa-biometric-${docKey}`}
                      status={getFingerStatus(item)}
                      isProcessingPdf={item.isProcessingPdf}
                      reason={item.reason}
                      fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
                      fileNames={item.fileNames}
                      label={`Previous Biometric Residence Permit ${
                        Object.keys(otherVisa.biometric ?? {}).length > 1 ? (index > 0 ? index + 1 : '') : ''
                      }`}
                      canUpload={infoOnly ? false : props.canUpload}
                      canOpen={infoOnly ? false : props.canOpen}
                      canApprove={infoOnly ? false : props.canApprove}
                      canReject={infoOnly ? false : props.canReject}
                      isDeleting={infoOnly ? false : props.isDeleting}
                      canAcknowledge={infoOnly ? props.canAcknowledge : false}
                      onUploadClick={() => props.onBiometricUploadClick(visasKey, docKey)}
                      onOpenClick={() => props.onBiometricOpenClick(visasKey, docKey)}
                      onApproveClick={() => props.onBiometricApproveClick(visasKey, docKey)}
                      onRejectClick={() => props.onBiometricRejectClick(visasKey, docKey)}
                      onRescindRejectClick={() => props.onBiometricRescindRejectClick(visasKey, docKey)}
                      onDeleteClick={() => props.onBiometricDeleteClick(visasKey, docKey)}
                      onAcknowledgeClick={() => props.onBiometricAcknowledgeClick(visasKey, docKey)}
                      userRole={props.userRole}
                      deletedByTTL={item.deletedByTTL}
                    >
                      {item.dateOfIssue && item.validUntil ? (
                        <>
                          <SummaryRow
                            key={`other-visa-bio-${docKey}-date-of-issue`}
                            label="Date of issue"
                            fullLabel="What is the date of issue?"
                            value={getFormatDateFromStoreDate(item.dateOfIssue)}
                          />
                          <SummaryRow
                            key={`other-visa-bio-${docKey}-valid-date`}
                            label="Valid until"
                            fullLabel="What is the valid until date?"
                            value={getFormatDateFromStoreDate(item.validUntil)}
                          />
                        </>
                      ) : (
                        <>
                          <SummaryRow
                            key={`other-visa-bio-${docKey}`}
                            label="Previous Biometric Residence Permit"
                            fullLabel="Did you receive a Biometric Residence Permit?"
                            value={`${boolToYesNo(item.hasDocument)}`}
                          />
                        </>
                      )}
                    </FingerWithApprove>
                  );
                }),
              );
            }
          }

          if (!hasDocs && !hasDeletedDocs) {
            return null;
          }

          if ((infoOnly || userAccess) && !hasDocs) {
            return null;
          }

          if (otherVisa.visa) {
            const visasArray: unknown = Array.from(Object.values(otherVisa.visa));
            const { visaType } = visasArray[0];
            const { year } = visasArray[0];
            return (
              <Section
                key={`other-visa-section-${visaType}-${year}`}
                label={`You indicated you had a ${getVisaLabelFromValue(visaType)} visa in ${year}`}
                canEdit={props.canResume}
                edit={() => {
                  props.onResume('history/step-2');
                }}
                warning={props.warning}
              >
                {hasDocs ? docNodes : <NoDocuments />}
              </Section>
            );
          }
        })}
    </>
  );
};

export default OtherVisa;
